/**
 * Clerk Configuration for DB Agent
 * 
 * Handles authentication and organization management
 */

/**
 * Feature flags for controlling application behavior
 */
export const featureFlags = {
  // Set to false to disable organization creation - users can only switch between existing organizations
  allowOrganizationCreation: false,
  // Set REACT_APP_COMING_SOON=true to show coming-soon feature previews
  comingSoon: process.env.REACT_APP_COMING_SOON === 'true',
};

export const clerkConfig = {
  // Clerk Publishable Key (safe to expose in frontend)
  publishableKey: process.env.REACT_APP_CLERK_PUBLISHABLE_KEY || '',
  
  // Appearance customization
  appearance: {
    elements: {
      rootBox: 'clerk-root',
      card: 'clerk-card',
    },
    layout: {
      socialButtonsPlacement: 'bottom',
      socialButtonsVariant: 'iconButton',
    },
    variables: {
      colorPrimary: '#3b82f6', // Tailwind blue-500
      colorBackground: '#ffffff',
      colorText: '#1f2937', // Tailwind gray-800
      colorTextSecondary: '#6b7280', // Tailwind gray-500
      colorDanger: '#ef4444', // Tailwind red-500
      borderRadius: '0.5rem',
    },
  },
  
  // Sign in configuration
  signIn: {
    routing: 'path',
    path: '/sign-in',
    // After sign in, redirect to organization selector if user has orgs
    redirectUrl: '/select-organization',
  },
  
  // Sign up configuration
  signUp: {
    routing: 'path',
    path: '/sign-up',
    // After sign up, redirect based on whether organization creation is allowed
    redirectUrl: featureFlags.allowOrganizationCreation ? '/create-organization' : '/select-organization',
  },
  
  // Organization configuration
  organization: {
    // Allow users to create organizations (controlled by feature flag)
    allowCreate: featureFlags.allowOrganizationCreation,
    
    // Show organization switcher in navigation (controlled by REACT_APP_SHOW_ORG_SWITCHER env var)
    showOrganizationSwitcher: process.env.REACT_APP_SHOW_ORG_SWITCHER === 'true',
    
    // Organization switcher appearance
    organizationSwitcher: {
      hidePersonal: false, // Allow personal workspace
      organizationProfileMode: 'navigation',
    },
  },
  
  // User button configuration
  userButton: {
    appearance: {
      elements: {
        avatarBox: 'h-10 w-10',
      },
    },
  },
};

/**
 * Organization roles configuration
 */
export const organizationRoles = {
  OWNER: 'org:owner',
  ADMIN: 'org:admin',
  MEMBER: 'org:member',
  VIEWER: 'org:viewer',
};

/**
 * Check if user has specific role
 */
export const hasRole = (userRole, requiredRole) => {
  const roleHierarchy = {
    'org:owner': 4,
    'org:admin': 3,
    'org:member': 2,
    'org:viewer': 1,
  };
  
  return (roleHierarchy[userRole] || 0) >= (roleHierarchy[requiredRole] || 0);
};

/**
 * Get organization ID from Clerk session
 */
export const getOrgId = (session) => {
  return session?.lastActiveOrganizationId || session?.user?.organizationMemberships?.[0]?.organization?.id;
};

/**
 * Check if user can perform action based on permission
 */
export const hasPermission = (membership, permission) => {
  if (!membership) return false;
  
  // Owners and admins have all permissions
  if (membership.role === organizationRoles.OWNER || membership.role === organizationRoles.ADMIN) {
    return true;
  }
  
  // Check custom permissions if available
  return membership.permissions?.includes(permission) || false;
};

